import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
  Skeleton,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useApiQuery } from '@shared/hooks/useApi.js';

const STATUS_META = {
  pending: { label: 'En attente', color: 'warning' },
  confirmed: { label: 'Confirmée', color: 'success' },
  cancelled: { label: 'Annulée', color: 'default' },
};

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return '—';
  }
}

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfHorizon(days = 7) {
  const d = startOfToday();
  d.setDate(d.getDate() + days);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Bloc prioritaire dashboard — prochaines réservations (aujourd’hui → +7 j.).
 */
export default function UpcomingBookingsPanel() {
  const { data, isLoading, isError } = useApiQuery('dashboard-upcoming-bookings', '/v1/bookings');

  const upcoming = useMemo(() => {
    const list = Array.isArray(data) ? data : (data?.items ?? []);
    const from = startOfToday().getTime();
    const to = endOfHorizon(7).getTime();
    return list
      .filter((b) => {
        if (!b?.startsAt || b.status === 'cancelled') return false;
        const t = new Date(b.startsAt).getTime();
        return t >= from && t <= to;
      })
      .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
      .slice(0, 5);
  }, [data]);

  return (
    <Paper
      sx={{
        p: { xs: 1.75, md: 2 },
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <EventAvailableIcon color="primary" fontSize="small" />
          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              Réservations à venir
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Aujourd’hui et 7 prochains jours
            </Typography>
          </Box>
        </Stack>
        <Button
          component={RouterLink}
          to="/bookings"
          size="small"
          endIcon={<ArrowForwardIcon />}
          sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
        >
          Voir tout
        </Button>
      </Stack>

      {isLoading && (
        <Stack spacing={1}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={48} />
          ))}
        </Stack>
      )}

      {!isLoading && isError && (
        <Typography variant="body2" color="text.secondary">
          Impossible de charger les réservations. Réessayez depuis la page Réservations.
        </Typography>
      )}

      {!isLoading && !isError && upcoming.length === 0 && (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Aucune réservation prévue sur les 7 prochains jours. Planning libre pour le moment.
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/bookings"
          >
            Nouvelle réservation
          </Button>
        </Box>
      )}

      {!isLoading && !isError && upcoming.length > 0 && (
        <List disablePadding dense>
          {upcoming.map((b) => {
            const meta = STATUS_META[b.status] || { label: b.status, color: 'default' };
            return (
              <ListItem
                key={b.id}
                disableGutters
                sx={{
                  px: 1,
                  py: 0.75,
                  borderRadius: 1,
                  mb: 0.5,
                  bgcolor: 'action.hover',
                  alignItems: 'flex-start',
                }}
                secondaryAction={
                  <Chip
                    size="small"
                    label={meta.label}
                    color={meta.color}
                    variant={meta.color === 'default' ? 'outlined' : 'filled'}
                    sx={{ mt: 0.5 }}
                  />
                }
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 600, pr: 10 }}>
                      {b.contact?.name || 'Sans contact'}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" component="span">
                      {formatWhen(b.startsAt)}
                      {b.resource?.name ? ` · ${b.resource.name}` : ''}
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </Paper>
  );
}
